import { useCallback, useEffect, useMemo, useState } from 'react'

import * as XLSX from 'xlsx'

import {
  Box, Stack, Typography, FormControl, Alert, Select, MenuItem,
  FormControlLabel, FormGroup, Checkbox,
  Button
} from "@mui/material"

import { CloudUpload as CloudUploadIcon } from "@mui/icons-material"

import { Section, SectionProps } from 'components/controls/section'
import { NumericInput } from 'components/controls/numbericInput'

import { SearchResult } from 'interface/search'
import { useGetEvaluationForm } from 'interface/evaluationForm'
import { EvaluationResultKojinRecord, EvaluationResultListFormData } from 'interface/search/evaluation'

const ACCEPT_FILETYPE_EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'

type ColumnInfo = {
  index: number
  name: string
  label: string
}
type FieldColumnPare = {
  fieldId: string
  columnName: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cellValue = (cell: any): string | null => {
  if (cell.w !== undefined && cell.w !== null) {
    if (typeof cell.w === 'string') return cell.w;
    if (typeof cell.w === 'number') return `${cell.w}`;
  }
  return null;
}

export const useEvaluationUploadForm = (
  search: SearchResult
) => {
  const formState = useGetEvaluationForm(search.evaluationFormId)
  const fields = useMemo(
    () => {
      if (!formState.data) return []
      const form = formState.data
      const fields: string[] = []
      
      for(const consideration of form.considerations) {
        if (consideration !== "") {
          fields.push(consideration)
        }
      }
      for(const section of form.sections) {
        for(const field of section.fields) {
          if (field.contentFieldId !== "") {
            fields.push(field.contentFieldId)
          }
          if (field.scoreFieldId !== "") {
            fields.push(field.scoreFieldId)
          }
        }
      }
      return fields
    },
    [formState.data]
  )

  const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const [workBook, setWorkBook] = useState<XLSX.WorkBook | null>(null)
  const [sheetNameList, setSheetNameList] = useState<string[]>([])
  const [sheetName, setSheetName] = useState<string>('')

  // formdata
  const [startRow, setStartRow] = useState<string>('1')
  const [hasHeader, setHasHeader] = useState<boolean>(true)
  const [columnList, setColumnList] = useState<ColumnInfo[]>([])

  const [kojinCodeColumn, setKojinCodeColumn] = useState<string>('')
  const [fieldColumns, setFieldColumns] = useState<FieldColumnPare[]>([])

  const isDisabled = useMemo<boolean>(
    () => {
      return file === null
    },
    [file]
  )

  // 選択したworkBook内のシート名の解析
  useEffect(
    () => {
      setSheetNameList(workBook?.SheetNames ?? [])
      setSheetName((workBook && workBook.SheetNames.length > 0) ? workBook.SheetNames[0] : '')
    },
    [workBook]
  )

  // 選択したシート内の解析
  useEffect(
    () => {
      const fieldColumns: FieldColumnPare[] = []
      if (workBook && sheetName) {
        const sheet = workBook.Sheets[sheetName]
        const columnList: ColumnInfo[] = []
        const headerRow = (parseInt(startRow) ?? 1) - 1
        for(let columnIndex = 0; columnIndex < 1000; columnIndex++) {
          const cellAddress = XLSX.utils.encode_cell({ c: columnIndex, r: headerRow })
          const cell = sheet[cellAddress]
          if (cell === null || cell === undefined) break
          columnList.push({
            index: columnIndex,
            name: XLSX.utils.encode_col(columnIndex),
            label: (hasHeader ? cellValue(cell) : null) ?? `Column ${columnIndex}`
          })
        }
        setColumnList(columnList)
        if (columnList.length > 0) {
          setKojinCodeColumn(columnList[0].name)
        }
        for(const fieldId of fields) {
          const column = columnList.find(column => column.label === fieldId)
          fieldColumns.push({ fieldId, columnName: column?.name ?? '' })
        }
      } else {
        setColumnList([])
      }
      setFieldColumns(fieldColumns)
    },
    [
      workBook, sheetName, startRow, hasHeader, fields,
      setColumnList
    ]
  )

  const submit = useCallback(
    (post: (postData: EvaluationResultListFormData) => void) => {
      let duplicate = 0
      const postData: EvaluationResultListFormData = { add: true, list: [] }
      if (!workBook) return

      const sheet = workBook.Sheets[sheetName]
      if (!sheet) return

      const kojinCodeIndex = columnList.find(column => column.name === kojinCodeColumn)?.index ?? null
      if (kojinCodeIndex === null) return

      const getCellValue = (column: number, row: number) => {
        const address = XLSX.utils.encode_cell({ c: column, r: row })
        const cell = sheet[address]
        if (cell === null || cell === undefined) return undefined
        const value = cellValue(cell)
        if (value === null) return undefined
        return value
      }
      for(let rowIndex = 1; rowIndex < 999999; rowIndex++) {
        const kojinCodeValue = getCellValue(kojinCodeIndex, rowIndex)
        if (kojinCodeValue === undefined) continue
          
        let kojinCode: string | null = null
        if (typeof kojinCodeValue === 'string') {
          kojinCode = kojinCodeValue
        } else if (typeof kojinCodeValue === 'number') {
          kojinCode = `${kojinCode}`
        }
        if (!kojinCode) continue
        if (kojinCode.length < 6) kojinCode = Array((6 - kojinCode.length) + 1).join('0') + kojinCode
        if (kojinCode.length > 6) kojinCode = kojinCode.substring(kojinCode.length - 6)

        const kojinRecord: EvaluationResultKojinRecord = {
          kojinCode,
          fields: []
        }
        for(const { fieldId, columnName } of fieldColumns) {
          const column = columnList.find(column => column.name === columnName)
          if (!column) continue
          const value = getCellValue(column.index, rowIndex)
          if (!value || value === "") continue
          kojinRecord.fields.push({ fieldId, value })
        }
        const at = postData.list.find(e => e.kojinCode === kojinCode)
        if (at) {
          at.fields = kojinRecord.fields
          duplicate = duplicate + 1
        } else {
          postData.list.push(kojinRecord)
        }
      }
      if (postData.list.length === 0) return
      post(postData)
    },
    [workBook, sheetName, columnList, fieldColumns, kojinCodeColumn]
  )

  return {
    form: {
      sheetName, setSheetName,
      startRow, setStartRow,
      hasHeader, setHasHeader,
      kojinCodeColumn, setKojinCodeColumn,
      fieldColumns, setFieldColumns
    },
    loading, setLoading,
    file, setFile,
    workBook, setWorkBook,
    sheetNameList,
    columnList,
    isDisabled,
    submit
  }
}

const sectionWidth = "8rem"

export const EvaluationUploadForm = ({
  form: {
    // formdata - file
    sheetName, setSheetName,
    startRow, setStartRow,
    hasHeader, setHasHeader,
    kojinCodeColumn, setKojinCodeColumn,
    fieldColumns, setFieldColumns
  },
  setLoading,
  file, setFile,
  sheetNameList, setWorkBook,
  columnList
}: ReturnType<typeof useEvaluationUploadForm>) => {
  const [fileError, setFileError] = useState<string | null>(null)

  const onChoiceFile = useCallback(
    (file: File | null) => {
      setFileError(null)
      setWorkBook(null)
      if (file) {
        const at = file.name.lastIndexOf('.')
        const ext = at < 0 ? '' : file.name.substring(at).toLowerCase()
        if (ext !== '.xlsx') {
          setFileError('ファイルの拡張子はxlsxのみ対応しています')
          return
        }
        try {
          setLoading(true)
          const reader = new FileReader()
          reader.onload = function (e) {
            if (!e?.target?.result || typeof e.target.result === 'string') return
            const unit8 = new Uint8Array(e.target.result)
            setWorkBook(XLSX.read(unit8, { type: 'array' }))
            setLoading(false)
          }
          reader.onerror = function () {
            throw new Error('ファイルの読込に失敗しました')
          }
          reader.readAsArrayBuffer(file)
        } catch (reason) {
          setFileError(`シートの読み込みに失敗しました : ${reason}`)
          setLoading(false);
          return
        }
      }
      setFile(file)
    },
    [setFileError, setFile, setWorkBook, setLoading]
  )
  const sectionProps = useMemo<SectionProps>(
    () => {
      return {
        labelWidth: sectionWidth,
        showLabel: file !== null ? true : false,
        labelProps: {
          paddingY: 0,
          justifyContent: "center"
        },
        disablePaddingY: true,
        innerProps: {
          paddingY: 0.5
        }
      }
    },
    [file]
  )
  return (
    <FormGroup>
      <Box width="100%">
        <Section label='ファイル' {...sectionProps}>
          <Button
            component="label" // buttonをlabelとして振る舞わせる
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{
              marginBottom: 1
            }}
          >
            クリックしてファイルを選択..
            <input
              type="file"
              hidden // input要素自体は表示しない
              accept={ACCEPT_FILETYPE_EXCEL}
              onChange={(event) => {
                if (event.target.files && event.target.files[0]) {
                  onChoiceFile(event.target.files[0])
                }
              }}
            />
          </Button>
          { fileError && <Box marginBottom={1}><Alert severity='warning'>{fileError}</Alert></Box> }
        </Section>
          
        { file !== null && (
          <Stack direction='column'>
            <Section label='読込範囲' {...sectionProps} labelProps={{ py: 1.5 }}>
              <Stack direction='column' spacing={1}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography variant='body2' component='div'>シート</Typography>
                  <FormControl size="small" sx={{ flexGrow: 1 }}>
                    <Select value={sheetName} onChange={(event) => setSheetName(event.target.value)}>
                      { sheetNameList.map((sheetName, i) => <MenuItem value={sheetName} key={`sheet-${i}`}>{sheetName}</MenuItem> )}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography variant='body2' component='div' whiteSpace='nowrap'>先頭行</Typography>
                  <NumericInput fullWidth state={startRow} setState={setStartRow}/>

                  <FormControlLabel
                    sx={{
                      whiteSpace: 'nowrap'
                    }}
                    control={
                      <Checkbox
                        checked={hasHeader}
                        size="small"
                        onChange={(event) => {
                          setHasHeader(event.target.checked)
                        }}
                      />
                    }
                    label='先頭行にヘッダー'
                  />
                </Stack>
              </Stack>
            </Section>

            <Section label='DCF個人コード' {...sectionProps}>
              <FormControl size="small">
                <Select value={kojinCodeColumn} onChange={(event) => setKojinCodeColumn(event.target.value)} size="small">
                  { columnList.map((column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem> )}
                </Select>
              </FormControl>
            </Section>

            {fieldColumns.map((column, i) => (
              <Section key={`field-${i}`} label={column.fieldId} {...sectionProps}>
                <FormControl size="small">
                  <Select
                    value={column.columnName}
                    size="small"
                    onChange={(event) => {
                      setFieldColumns(
                        fieldColumns.map(
                          exist => exist.fieldId === column.fieldId ? {
                            fieldId: exist.fieldId,
                            columnName: event.target.value
                          } : exist
                        ).slice()
                      )
                    }}
                  >
                    <MenuItem value='none'>（なし）</MenuItem>
                    {columnList.map(
                      (column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Section>
            ))}
          </Stack>
        )}
      </Box>
    </FormGroup>
  )
}
